<script>
import _pick from 'lodash/pick';
import { mapGetters } from 'vuex';
import { genericForms } from '@shared/mixins/forms';
import aiAssistantMixin from '@app/mixins/aiAssistant';
import APIStore from '@app/services/API/Store';
import AILoader from '@app/components/AILoader.vue';

export default {
  mixins: [genericForms, aiAssistantMixin],
  components: {
    AILoader,
  },
  props: {
    hasExplanation: {
      type: Boolean,
      default: true,
    },
  },
  static: {
    cost: 3,
  },
  form: {
    what: {
      type: 'text',
      column: 'is-12',
      label: 'Quel est le titre ou la thématique de votre formation ? (obligatoire)',
      tooltipAttrs: { multilined: true },
      tooltipHTML: `
        <p>
          Afin de rédiger le meilleur plan pour votre page de vente,
          j'ai besoin du titre de votre formation, ou au moins
          de sa thématique.
        </p>
        <p class="mt-2">
          Vous pouvez aussi mettre plusieurs mots-clés séparés
          par des virgules.
        </p>
      `,
      inputAttrs: {
        placeholder: 'Apprendre le SEO en 5 étapes',
        required: true,
        maxlength: 100,
      },
    },
    tone_1: {
      type: 'select',
      column: 'is-12',
      label: 'Quel ton voulez-vous donner au texte ?',
      options: {
        empty: 'Aléatoire',
        familiarity: 'Tutoiement',
        formality: 'Vouvoiement',
      },
      inputAttrs: {
        required: true,
      },
    },
    who: {
      type: 'text',
      column: 'is-12',
      label: 'À qui s\'adresse votre formation ?',
      tooltipAttrs: { multilined: true },
      tooltip: `
        Une page de vente efficace doit s'adresser
        à votre coeur de cible de la manière la plus précise possible.
      `,
      inputAttrs: {
        placeholder: 'les entrepreneurs qui ne veulent pas faire de publicité payante',
        maxlength: 120,
      },
    },
    problem: {
      type: 'text',
      column: 'is-12',
      label: 'Quel problème, rencontré par votre cible, cette formation résout-elle ?',
      tooltipAttrs: { multilined: true },
      tooltip: `
        Expliquez en une phrase le problème ou le besoin qu'a
        votre cible et que votre formation permettra de résoudre.
      `,
      inputAttrs: {
        placeholder: 'Le SEO, c\'est long et très compliqué à apprendre',
        maxlength: 120,
      },
    },
    result: {
      type: 'textarea',
      column: 'is-12',
      label: 'Quel sera le bénéfice pour l\'acheteur ?',
      tooltipAttrs: { multilined: true },
      tooltip: `
        Expliquez en une phrase les bénéfices de votre formation
        pour votre cible.
      `,
      inputAttrs: {
        placeholder: 'Vous saurez attirer plus de clients',
        rows: 2,
        maxlength: 240,
      },
    },
  },
  data() {
    return {
      newOptions: {
        type: 'outline',
        what: '',
        who: '',
        problem: '',
        result: '',
        tone_1: 'empty',
        tones: [],
      },
    };
  },
  computed: {
    ...mapGetters('auth', {
      authStore: 'store',
    }),
    baseFields() {
      return _pick(this.$options.form, [
        'style',
        'what',
        'tone_1',
      ]);
    },
    advancedFields() {
      const fields = { ...this.$options.form };

      delete fields.what;
      delete fields.tone_1;

      return fields;
    },
  },
  methods: {
    action() {
      const newOptions = { ...this.newOptions };

      newOptions.tones = [];

      if (newOptions.tone_1 !== 'empty') {
        newOptions.tones.push(newOptions.tone_1);
      }

      delete newOptions.tone_1;

      Object.keys(newOptions)
        .forEach((v) => newOptions[v] === '' && (delete newOptions[v]));

      return APIStore.generateAIContent(this.authStore.uuid, newOptions)
        .then(({ data }) => {
          this.showContent(data);
          this.removeCredits();
        });
    },
  },
};
</script>

<template>
  <div>
    <template v-if="hasExplanation">
      <h3 class="title is-5">
        Rédigez un plan de page de vente
        <b-tooltip position="is-left">
          <template #content>
            <span v-t="'ai_assistant.credit_cost'" />
          </template>
          <span class="tag is-outlined">
            {{ $options.static.cost }}
            crédit{{ $options.static.cost > 1 ? 's' : '' }}
          </span>
        </b-tooltip>
      </h3>
      <div class="content">
        <p>
          Vous souhaitez rédiger votre page de vente vous-même,
          mais vous ne savez pas comment la structurer ?
        </p>
        <p>
          Pas de soucis, je vais rédiger un plan spécialement
          pour votre page de vente !
        </p>
      </div>
    </template>

    <form
      class="box"
      @submit.prevent="dataIsValid(isLoading) && hasEnoughCredits() && handle()"
    >
      <div class="columns is-multiline">
        <div
          v-for="(field, k) in baseFields"
          :key="k"
          class="column mb-0"
          :class="field.column"
        >
          <GeneratorField
            :fieldName="k"
            :fieldDefinition="field"
            :formData.sync="newOptions"
          />
        </div>
      </div>
      <AppToggleSection>
        <template #title>
          Avez-vous plus de précisions ?
        </template>
        <div class="columns is-multiline">
          <div
            v-for="(field, k) in advancedFields"
            :key="k"
            class="column mb-0"
            :class="field.column"
          >
            <GeneratorField
              :fieldName="k"
              :fieldDefinition="field"
              :formData.sync="newOptions"
            />
          </div>
        </div>
      </AppToggleSection>
      <div class="field has-text-right">
        <b-button
          type="is-primary"
          native-type="submit"
          icon-left="magic"
          :loading="isLoading"
        >
          Générer un plan de page
        </b-button>
      </div>
    </form>

    <transition name="fade">
      <AILoader v-if="isLoading" />
    </transition>
  </div>
</template>
